import React, { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from '../../../../Store';
import { colors, glyphs, useMediaQuery } from '../../../../Styles';
import { ButtonList, NewInputList, NewInputListHandles} from '../../../../Components';
import { ListStyle_Types, NewFieldStyles } from '../../../../Definitions';
import { useUser } from "../../../../Services"
import { Farm_Data, User_Data } from '../../../../Services/Agrisys_GqlDataCaller/UserCalls/GQLCalls/getCalls';

// TODO
import Setting from '../../../../Components/Settings/setting';

export default function UserAndFarmSettings() {
  const {t} = useTranslation()
  const { userToken, getUserData, userData, updateUserData, getFarmData, farmData: farmDataArray, updateFarmData, getFarmSettings } = useUser() 
  const userInputRef=useRef<NewInputListHandles>(null)
  const farmInputRef=useRef<NewInputListHandles>(null)
  const mobileXs = useMediaQuery(`(max-width: 570px)`);
  const responsiveBox = useMediaQuery(`(max-width: 1420px)`);

  const farm = useAppSelector(({activeData}) => activeData.farm) ?? "";

  useEffect(() => {
    if (userToken) {
      getUserData(userToken);
      if (farm) {
        getFarmData(farm);
        getFarmSettings(farm);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [farm]); 

  // #region | User Settings
  const loadUserData = useCallback(() => {
    if (userData !== undefined) {
      userInputRef.current?.refs.current[0].setValue(userData.firstname)
      userInputRef.current?.refs.current[1].setValue(userData.lastname)
      userInputRef.current?.refs.current[2].setValue(userData.phone)
      userInputRef.current?.refs.current[3].setValue(userData.email)
      userInputRef.current?.refs.current[4].setValue(userData.address)
      userInputRef.current?.refs.current[5].setValue(userData.country)
      userInputRef.current?.refs.current[6].setValue(userData.username)
    }
  }, [userData])

  useEffect(() => {
    loadUserData();
  }, [loadUserData]);

  const handleUserDataUpdate = () => {
    var NewUserData = {
      firstname: userInputRef.current?.refs.current[0].value ?? "",
      lastname: userInputRef.current?.refs.current[1].value ?? "",
      phone: userInputRef.current?.refs.current[2].value ?? "",
      email: userInputRef.current?.refs.current[3].value ?? "",
      address: userInputRef.current?.refs.current[4].value ?? "",
      country: userInputRef.current?.refs.current[5].value ?? ""
    } as User_Data
    updateUserData(NewUserData.firstname, NewUserData.lastname, NewUserData.phone, NewUserData.email, NewUserData.address, NewUserData.country)
  }
  // #endregion

  // #region | Farm Settings
  const loadFarmData = useCallback(() => {
    if (farmDataArray !== undefined) {
      const farmData = farmDataArray[0];
      farmInputRef.current?.refs.current[0].setValue(farmData.name);
      farmInputRef.current?.refs.current[1].setValue(farmData.city);
      farmInputRef.current?.refs.current[2].setValue(farmData.street);
      farmInputRef.current?.refs.current[3].setValue(farmData.phone);
      farmInputRef.current?.refs.current[4].setValue(farmData.vat);
      farmInputRef.current?.refs.current[5].setValue(farmData.email);
    }
  }, [farmDataArray]);
  
  useEffect(() => {
    loadFarmData();
  }, [loadFarmData]);

  const handleFarmDataUpdate = () => {
    var NewFarmData = {
      name: farmInputRef.current?.refs.current[0].value ?? "",
      city: farmInputRef.current?.refs.current[1].value ?? "",
      street: farmInputRef.current?.refs.current[2].value ?? "",
      phone: farmInputRef.current?.refs.current[3].value ?? "",
      vat: farmInputRef.current?.refs.current[4].value ?? "",
      email: farmInputRef.current?.refs.current[5].value ?? ""
    } as Farm_Data
    updateFarmData( NewFarmData.name, NewFarmData.city, NewFarmData.street, NewFarmData.phone, NewFarmData.vat, NewFarmData.email, farm)
  }
  // #endregion

  return ( <div style={{display: "flex", gap: "20px", alignItems: mobileXs ? "center" : "end", justifyContent: "center", flexDirection: "column", width: !responsiveBox ? "1050px" : "100%"}}>
    {/* User Settings */}
    <Setting startEnhancer={glyphs.User({size: "25px"})} header={t("user.userInformation", {defaultValue: "user information"})} content={
      <NewInputList ref={userInputRef}
        placeholders={["", "", "", "", "", "", ""]}
        headers={[
          t("user.firstName", {defaultValue: "First Name"}), 
          t("user.lastName", {defaultValue: "Last Name"}),
          t("user.phone", {defaultValue: "Phone"}),
          t("user.email", {defaultValue: "Email"}), 
          t("user.country", {defaultValue: "Country"}),
          t("user.address", {defaultValue: "Address"}),
          t("user.userName", {defaultValue: "User Name"})
        ]}
        isRequired={Array.from({ length: 7 }, () => false)}
        isDisabled={Array.from({ length: 7 }, (_, index) => index === 6)}
        fieldOverrides={{
          fieldStyle: NewFieldStyles.greyTertiary
        }}
        listOverrides={{
          listStyle: mobileXs ? ListStyle_Types.centeredGroup : ListStyle_Types.group
        }}
      />
    }/>

    {/* Farm Settings */}
    <Setting startEnhancer={glyphs.Animal({size: "25px"})} header={t("farm.farmInformation", {defaultValue: "farm information"})} content={
      <NewInputList ref={farmInputRef} 
        placeholders={["", "", "", "", "", ""]}
        headers={[
          t("farm.farmName", {defaultValue: "farm name"}), 
          t("farm.city", {defaultValue: "city"}),
          t("farm.street", {defaultValue: "street"}),
          t("farm.phone", {defaultValue: "phone"}),
          t("farm.vat", {defaultValue: "vat"}),
          t("farm.email", {defaultValue: "email"})
        ]}
        isRequired={Array.from({ length: 6 }, () => true)} 
        isDisabled={Array.from({ length: 6 }, (_, index) => index === 0)}
        fieldOverrides={{
          fieldStyle: NewFieldStyles.greyTertiary
        }}
        listOverrides={{
          listStyle: mobileXs ? ListStyle_Types.centeredGroup : ListStyle_Types.group
        }}
      />
    } />

    {/* Save/discard btns */}
    <ButtonList 
      contents={[t("globalSettingsPages.globalSettings.saveChanges", {defaultValue: "save changes"}), t("globalSettingsPages.globalSettings.discardChanges", {defaultValue: "discard changes"})]} 
      onClick={(i => {
        if (i === 0) {handleUserDataUpdate(); handleFarmDataUpdate()}
        else if (i === 1) {loadUserData(); loadFarmData()}
      })}
      overrides={{
        baseButton: {width: "fit-content", height: "fit-content", padding: "10px", backgroundColor: colors.greyPrimary},
        hoverStyles: {backgroundColor: colors.greySecondary},
        endEnhancerStyles: {marginLeft: "5px"}
      }}      
    />
  </div> );
}